import { Add, Delete, GridOn, HorizontalRule, UnfoldMore } from "@mui/icons-material";
import { Box, Button, Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Popover, Stack, TableContainer, TextField, Tooltip, Typography } from "@mui/material";
import { Editor } from "@tiptap/react";
import { useEffect, useState } from "react";


interface NoteObjectsProps {
    editor: Editor;
    mode: number;
}

export default function NoteObjects({ editor, mode }: NoteObjectsProps) {
    const [tableAnchorEl, setTableAnchorEl] = useState<null | HTMLElement>(null);
    const [rows, setRows] = useState('3');
    const [cols, setCols] = useState('3');

    const [contextMenu, setContextMenu] = useState<{
        mouseX: number;
        mouseY: number;
        cellPos?: number;
    } | null>(null);

    const handleInsertTable = (event: React.MouseEvent<HTMLElement>) => {
        setTableAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setTableAnchorEl(null);
    };
    
    const open = Boolean(tableAnchorEl);
    
    const insertTable = () => {
        editor.chain().focus().insertTable({
            rows: Math.max(1, Number(rows)),
            cols: Math.max(1, Number(cols)),
            withHeaderRow: true,
        }).run();

        handleClose();
    };

    useEffect(() => {
        const handleContextMenu = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            const cell = target.closest('td, th');
            if (cell) {
                event.preventDefault();
            
                const view = editor.view;
                const pos = view.posAtDOM(cell, 0);
            
                setContextMenu({
                    mouseX: event.clientX,
                    mouseY: event.clientY,
                    cellPos: pos,
                });
            }
        };
      
        const dom = editor.view.dom;
        dom.addEventListener('contextmenu', handleContextMenu);
      
        return () => {
            dom.removeEventListener('contextmenu', handleContextMenu);
        };
      }, [editor]);

    return (
        <>
            <Tooltip title="Table">
                <IconButton size="small" onClick={handleInsertTable}
                    sx={{
                        color: mode === 1 ? 'inherit' : 'white'
                    }}
                >
                    <GridOn sx={{ fontSize: 18 }} />
                </IconButton>
            </Tooltip>

            <Tooltip title="horizontal line">
                <IconButton
                    size="small"
                    onClick={() => editor.chain().focus().setHorizontalRule().run()}
                    sx={{
                        color: mode === 1 ? 'inherit' : 'white'
                    }}
                >
                    <HorizontalRule sx={{ fontSize: 18 }} />
                </IconButton>
            </Tooltip>
            
            <Menu
                open={!!contextMenu}
                onClose={() => setContextMenu(null)}
                anchorReference="anchorPosition"
                anchorPosition={
                    contextMenu
                    ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                    : undefined
                }
            >
                <MenuItem onClick={() => {
                    editor.chain().focus().addColumnBefore().run();
                    setContextMenu(null);
                }}>
                    <ListItemIcon><Add fontSize="small" /></ListItemIcon>
                    <ListItemText>Insert Column Left</ListItemText>
                </MenuItem>

                <MenuItem onClick={() => {
                    editor.chain().focus().addColumnAfter().run();
                    setContextMenu(null);
                }}>
                    <ListItemIcon><Add fontSize="small" /></ListItemIcon>
                    <ListItemText>Insert Column Right</ListItemText>
                </MenuItem>

                <MenuItem onClick={() => {
                    editor.chain().focus().deleteColumn().run();
                    setContextMenu(null);
                }}>
                    <ListItemIcon><Delete fontSize="small" /></ListItemIcon>
                    <ListItemText>Delete Column</ListItemText>
                </MenuItem>

                <Divider />

                <MenuItem onClick={() => {
                    editor.chain().focus().addRowBefore().run();
                    setContextMenu(null);
                }}>
                    <ListItemIcon><Add fontSize="small" /></ListItemIcon>
                    <ListItemText>Insert Row Above</ListItemText>
                </MenuItem>

                <MenuItem onClick={() => {
                    editor.chain().focus().addRowAfter().run();
                    setContextMenu(null);
                }}>
                    <ListItemIcon><Add fontSize="small" /></ListItemIcon>
                    <ListItemText>Insert Row Below</ListItemText>
                </MenuItem>

                <MenuItem onClick={() => {
                    editor.chain().focus().deleteRow().run();
                    setContextMenu(null);
                }}>
                    <ListItemIcon><Delete fontSize="small" /></ListItemIcon>
                    <ListItemText>Delete Row</ListItemText>
                </MenuItem>

                <Divider />

                <MenuItem onClick={() => {
                    editor.chain().focus().setCellAttribute('colspan', 2).run();
                    setContextMenu(null);
                }}>
                    <ListItemIcon><UnfoldMore fontSize="small" /></ListItemIcon>
                    <ListItemText>Extend Cell (colspan)</ListItemText>
                </MenuItem>

                <Divider />


                <MenuItem onClick={() => {
                    editor.chain().focus().deleteTable().run();
                    setContextMenu(null);
                }}>
                    <ListItemIcon><Delete fontSize="small" /></ListItemIcon>
                    <ListItemText>Delete Table</ListItemText>
                </MenuItem>
            </Menu>


            <Popover
                open={open}
                anchorEl={tableAnchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Stack direction="row" spacing={1} sx={{ p: 2 }}>
                    <TextField
                        label="Rows"
                        type="number"
                        size="small"
                        value={rows}
                        onChange={(e) => setRows(e.target.value)}
                        inputProps={{ min: 1 }}
                        sx={{ width: '100px' }}
                    />
                    <TextField
                        label="Columns"
                        type="number"
                        size="small"
                        value={cols}
                        onChange={(e) => setCols(e.target.value)}
                        inputProps={{ min: 1 }}
                        sx={{ width: '100px' }}
                    />
                    <Button variant="contained" onClick={insertTable}>
                        Insert
                    </Button>
                </Stack>
            </Popover>
        </>
    );
}
